import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router';

import EmbeddedIFrame from 'views/Frames/EmbeddedIFrame';
import { updateNotifications } from 'reducers/notificationReducer';
import { GlobalState } from 'reducers/stateTypes';
import { ClmPortalIncomingPostMessageTypes, ClmPortalOutgoingPostMessageTypes } from 'utils/postMessageUtil';
import { useRuntimeConfig } from 'config/useRuntimeConfig';
import PageTitleToken from 'components/Common/PageTitleToken';
import SecondNavigationBar from 'components/Navigation/SecondNavigationBar';
import { Typography } from '@mui/material';
import theme from 'theme';
import { User, UserAuthority } from 'types/User';
import { getRouteIfSameOrigin, isNotNullOrUndefined, removeSearchParams } from 'utils/utils';
import { useTaskViewerFeatures } from 'hooks/queries/useTaskViewerFeatures';
import { usePostMessageToIFrame } from 'hooks/usePostMessageToIFrame';
import { useElementHeight } from 'hooks/useElementHeight';
import { TOPBAR_HEIGHT } from 'services/constants';
import { ZfTab, ZfTabList } from '@zafire/react';
import { useHost } from 'contexts/Host';
import { routeList } from 'Routes';

const secondNavigationStyles = {
  title: {
    margin: theme.spacing(3, 0, 1, 0),
  },
};

enum HomeTabs {
  TASKS = 'tasks',
  MANAGE_TASKS = 'manage-tasks',
  TOOLS = 'tools',
  WORKFLOWS = 'workflows',
  ADHOC_SCREENING = 'adhoc-screening',
  CONTACT_NOTES = 'contact-notes',
}

enum TaskTabs {
  MY_TASKS = 'my-tasks',
}

type HomeParams = {
  tab?: HomeTabs;
  subTab?: TaskTabs;
  requestUId?: string;
  batchId?: string;
};

// Check if user has ad hoc screening access
export function hasAdHocScreeningAccess(user: User): boolean {
  if (!user?.userAuthorities?.partner) return false;
  return user.userAuthorities.partner.includes(UserAuthority.ADHOC_SCREENING_ACCESS);
}

const Home = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    tab: selectedTabFromUrl,
    subTab: selectedSubTabFromUrl,
    requestUId: requestUIdFromPath,
    batchId: bulkUploadBatchID,
  } = useParams<HomeParams>();
  const [_searchParams, setSearchParams] = useSearchParams();
  const { postMessageToIFrame } = usePostMessageToIFrame();
  const homeTabIFrameRef = useRef<HTMLIFrameElement>(null);
  const [secondNavRef, secondNavHeight] = useElementHeight<HTMLDivElement>();
  const clickRefresh = useSelector((state: GlobalState) => state.notifications.clickRefresh);
  const user = useSelector((state: GlobalState) => state.user.userDetails);
  const { isStandalone } = useHost();

  const runtimeConfig = useRuntimeConfig();
  const { isWena } = useHost();
  const clmPortalUrl = runtimeConfig.configData.iframeUrls.clmPortalIframe;
  const pmkTaskViewerUrl = runtimeConfig.configData.iframeUrls.pmkTaskViewerIframe;

  // Use React Query hook for feature availability
  const { data: features, isLoading: isCheckingAccess } = useTaskViewerFeatures();

  // Extract features with defaults for error handling
  const hasCaseManagerTab = features?.caseManagerTab ?? false;
  const hasWorkflowsTab = features?.workflowsTab ?? false;
  const hasContactNotesTab = runtimeConfig.configData.featureFlags?.contactNote?.enabled ?? false;

  // Get query parameters for task-related functionality
  const queryParams = new URLSearchParams(location.search);
  // Get requestUId from path parameter first, then fall back to query parameter
  const requestUId = requestUIdFromPath || queryParams.get('requestUId');
  const contactNoteId = queryParams.get('contactNoteId');

  // Handle the case where we're on the specific adhoc-screening route
  // which doesn't capture :tab parameter, so we need to extract it from pathname
  const actualSelectedTab =
    selectedTabFromUrl ?? (location.pathname.includes('/adhoc-screening') ? HomeTabs.ADHOC_SCREENING : undefined);

  const selectedTab = actualSelectedTab || HomeTabs.TASKS;

  const handleRedirect = useCallback(
    (tab: HomeTabs, subTab?: TaskTabs) => {
      let path = `/home/${tab}`;

      if (tab === HomeTabs.TASKS && isNotNullOrUndefined(subTab)) {
        path += `/${subTab}`;
      }

      const pathWithoutSearchParams = removeSearchParams(path);
      navigate(pathWithoutSearchParams);
    },
    [navigate],
  );

  const handleTabChange = (selectedTabName: string) => {
    if (selectedTabName === HomeTabs.TASKS) {
      handleRedirect(selectedTabName as HomeTabs, TaskTabs.MY_TASKS);
    } else {
      handleRedirect(selectedTabName as HomeTabs);
    }
  };

  useEffect(
    function handleRedirects() {
      // Don't redirect while still checking access or user is not loaded yet
      if (isCheckingAccess || !isNotNullOrUndefined(user)) return;

      // Access control redirects - redirect to default when user doesn't have access
      if (selectedTabFromUrl === HomeTabs.MANAGE_TASKS && !hasCaseManagerTab) {
        handleRedirect(HomeTabs.TASKS, TaskTabs.MY_TASKS);
        return;
      }
      if (selectedTabFromUrl === HomeTabs.WORKFLOWS && !hasWorkflowsTab) {
        handleRedirect(HomeTabs.TASKS, TaskTabs.MY_TASKS);
        return;
      }
      if (selectedTabFromUrl === HomeTabs.ADHOC_SCREENING && !hasAdHocScreeningAccess(user)) {
        handleRedirect(HomeTabs.TASKS, TaskTabs.MY_TASKS);
        return;
      }
    },
    [
      selectedTabFromUrl,
      selectedSubTabFromUrl,
      handleRedirect,
      user,
      location.pathname,
      hasCaseManagerTab,
      hasWorkflowsTab,
      isCheckingAccess,
    ],
  );

  const clickRefreshRef = useRef(clickRefresh);
  clickRefreshRef.current = clickRefresh;

  useEffect(
    function handleIFrameCommunicationUpwards() {
      function handleUrlParamsChanged(event: MessageEvent) {
        const params = new URLSearchParams(event.data.value).toString();
        const message = event.data.message;

        if (message === ClmPortalIncomingPostMessageTypes.WORKFLOW_DASHBOARD_URL_PARAMS_CHANGED) {
          navigate({
            pathname: `/home/${HomeTabs.WORKFLOWS}`,
            search: params ? `?${params}` : '',
          });
          return;
        }

        if (message === ClmPortalIncomingPostMessageTypes.TASK_VIEWER_URL_PARAMS_CHANGED) {
          const taskSubTab = selectedSubTabFromUrl ?? TaskTabs.MY_TASKS;

          navigate({
            pathname: `/home/${HomeTabs.TASKS}/${taskSubTab}`,
            search: params ? `?${params}` : '',
          });
        }
      }

      function handleIFrameReady(tab: HomeTabs, outgoingMessage: ClmPortalOutgoingPostMessageTypes) {
        if (selectedTab !== tab) {
          return;
        }
        // In WeNa the app renders inside a shadow root, which document lookups cannot see into
        const iframe = homeTabIFrameRef.current ?? undefined;
        const payload = {
          message: outgoingMessage,
          value: location.search,
        };
        postMessageToIFrame(payload, iframe);
      }

      window.addEventListener('message', handleMessage);

      function handleMessage(event: MessageEvent) {
        const message = event.data.message;
        if (message === ClmPortalIncomingPostMessageTypes.REDIRECT_TO_PARTNER) {
          const { partnerUid, wizardBrUid, wizardRole } = event.data.value;
          const partnerUrl = `/partner/edit/${partnerUid}/core-data?wizardBrUid=${wizardBrUid ?? ''}&wizardRole=${wizardRole ?? ''}`;
          if (isWena) {
            window.dispatchEvent(new CustomEvent('WN_NAVIGATE', { detail: partnerUrl }));
            return;
          }

          window.open(partnerUrl, '_blank')?.focus();
          return;
        }
        if (message === ClmPortalIncomingPostMessageTypes.NEW_TAB_BR_CORE_DATA) {
          const { brId, brSource } = event.data.value;
          const brUrl = `${routeList.brCoreData(brId)}${brSource === 'CRM_CH_BR' ? '?brSource=CRM' : ''}`;
          if (isWena) {
            window.dispatchEvent(new CustomEvent('WN_NAVIGATE', { detail: brUrl }));
            return;
          }

          window.open(brUrl, '_blank')?.focus();
          return;
        }
        if (message === ClmPortalIncomingPostMessageTypes.OPEN_NEW_BR_TAB) {
          const { brId, brTab } = event.data.value;
          const brUrl = `/business-relation/edit/${brId}/${brTab}`;

          window.open(brUrl, '_blank')?.focus();
          return;
        }
        if (
          message === ClmPortalIncomingPostMessageTypes.VIEW_TASK ||
          message === ClmPortalIncomingPostMessageTypes.WORK_ON_TASK
        ) {
          const { url } = event.data;
          if (isWena) {
            if (!url) return;

            // WeNa is only informed about CLM urls, any other application opens in a new window
            const route = getRouteIfSameOrigin(url, clmPortalUrl);
            if (route) {
              window.dispatchEvent(new CustomEvent('WN_NAVIGATE', { detail: route }));
              return;
            }
          }
          window.open(url, '_blank')?.focus();
          return;
        }
        if (message === ClmPortalIncomingPostMessageTypes.OPEN_WORKFLOW) {
          const { workflowId } = event.data.value;
          const query = new URLSearchParams({
            tab: 'all-workflows',
            workflowId,
            isWorkflowShownOnDashboard: 'true',
          });
          const workflowUrl = `${routeList.home(HomeTabs.WORKFLOWS)}?${query}`;
          if (isWena) {
            window.dispatchEvent(new CustomEvent('WN_NAVIGATE', { detail: workflowUrl }));
            return;
          }

          window.open(workflowUrl, '_blank')?.focus();
          return;
        }
        if (message === ClmPortalIncomingPostMessageTypes.UPDATE_NOTIFICATIONS) {
          window.history.replaceState(null, 'CLM', event.data.value.url);
          dispatch(
            updateNotifications({
              currentTaskId: event.data.value.currentTaskId,
              clickRefresh: clickRefreshRef.current + 1,
            }),
          );
        }
        if (message === ClmPortalIncomingPostMessageTypes.COPY_TO_CLIPBOARD_FROM_TASK_VIEWER) {
          navigator.clipboard.writeText(event.data.value.taskId);
        }
        if (
          message === ClmPortalIncomingPostMessageTypes.TASK_VIEWER_URL_PARAMS_CHANGED ||
          message === ClmPortalIncomingPostMessageTypes.WORKFLOW_DASHBOARD_URL_PARAMS_CHANGED
        ) {
          handleUrlParamsChanged(event);
        }
        if (message === ClmPortalIncomingPostMessageTypes.TASK_VIEWER_READY) {
          handleIFrameReady(HomeTabs.TASKS, ClmPortalOutgoingPostMessageTypes.TASK_VIEWER_SEND_URL_PARAMS);
        }
        if (message === ClmPortalIncomingPostMessageTypes.WORKFLOW_DASHBOARD_READY) {
          handleIFrameReady(HomeTabs.WORKFLOWS, ClmPortalOutgoingPostMessageTypes.WORKFLOW_DASHBOARD_SEND_URL_PARAMS);
        }
      }
      return () => {
        window.removeEventListener('message', handleMessage);
      };
    },
    [dispatch, navigate, selectedSubTabFromUrl, clmPortalUrl],
  );

  const getTabUrl = (tab: HomeTabs) => {
    const caseManagerUrl = `${runtimeConfig.configData.iframeUrls.caseManagerIframe}`;
    const workflowsUrl = runtimeConfig.configData.iframeUrls.pmkWorkflowDashboardIframe;
    const contactNoteIdQueryParam = contactNoteId ? `?contactNoteId=${encodeURIComponent(contactNoteId)}` : '';
    const contactNoteBaseUrl = `${runtimeConfig.configData.iframeUrls.contactNoteIframe}/contact-notes/home`;
    const contactNoteHomeUrl = `${contactNoteBaseUrl}${contactNoteIdQueryParam}`;

    const adHocScreeningSuffix = bulkUploadBatchID
      ? `/bulk/${bulkUploadBatchID}/status`
      : requestUId
        ? `/${requestUId}`
        : '';
    const adHocScreeningUrl = `${runtimeConfig.configData.iframeUrls.lscServiceIframe}/adhoc-screening${adHocScreeningSuffix}`;
    const toolsUrl = `${runtimeConfig.configData.iframeUrls.accountRelationsServiceIframe}/tools`;

    // Main tab URLs
    switch (tab) {
      case HomeTabs.TASKS:
        return pmkTaskViewerUrl;
      case HomeTabs.MANAGE_TASKS:
        return caseManagerUrl;
      case HomeTabs.WORKFLOWS:
        return workflowsUrl;
      case HomeTabs.TOOLS:
        return toolsUrl;
      case HomeTabs.ADHOC_SCREENING:
        return adHocScreeningUrl;
      case HomeTabs.CONTACT_NOTES:
        return contactNoteHomeUrl;
      default:
        return pmkTaskViewerUrl;
    }
  };
  return (
    <>
      <PageTitleToken />
      <div ref={secondNavRef}>
        <SecondNavigationBar>
          {isStandalone && (
            <Typography variant="h1" color="primary" sx={secondNavigationStyles.title}>
              {t('topbar.home.title')}
            </Typography>
          )}

          <ZfTabList
            variant="default"
            value={selectedTab}
            role="tabList"
            data-testid="home-tab-list"
            style={{ paddingTop: 'var(--padding-vertical-xs)' }}
            onTabChange={(event) => handleTabChange(event.detail)}
          >
            <ZfTab
              label={t('home.tabs.tasks', 'Tasks')}
              role="tab"
              data-testid="home-tasks-tab"
              value={HomeTabs.TASKS}
              id="home-tasks-tab"
            />
            {hasCaseManagerTab && (
              <ZfTab
                label={t('home.tabs.manageTasks', 'Manage Tasks')}
                value={HomeTabs.MANAGE_TASKS}
                aria-label="Manage Tasks"
                role="tab"
                data-testid="home-manage-tasks-tab"
                id="home-manage-tasks-tab"
              />
            )}
            {hasWorkflowsTab && (
              <ZfTab
                label={t('home.tabs.workflows', 'Workflows')}
                value={HomeTabs.WORKFLOWS}
                role="tab"
                data-testid="home-workflows-tab"
                id="home-workflows-tab"
              />
            )}
            <ZfTab
              aria-label="Tools"
              label={t('home.tabs.tools', 'Tools')}
              role="tab"
              data-testid="home-tools-tab"
              value={HomeTabs.TOOLS}
              id="home-tools-tab"
            />
            {hasAdHocScreeningAccess(user) && (
              <ZfTab
                label={t('adhocScreen', 'Ad hoc screening')}
                value={HomeTabs.ADHOC_SCREENING}
                role="tab"
                data-testid="home-ad-hoc-screening-tab"
                id="home-ad-hoc-screening-tab"
              />
            )}
            {hasContactNotesTab && (
              <ZfTab
                label={t('home.tabs.contactNotes', 'Contact Notes')}
                role="tab"
                data-testid="home-contact-notes-tab"
                value={HomeTabs.CONTACT_NOTES}
                id="home-contact-notes-tab"
              />
            )}
          </ZfTabList>
        </SecondNavigationBar>
      </div>

      <EmbeddedIFrame
        url={getTabUrl(selectedTab)}
        title={`Home - ${selectedTab}`}
        id="home-tab-iframe"
        height={`calc(100% - ${TOPBAR_HEIGHT + secondNavHeight}px)`}
        iframeRef={homeTabIFrameRef}
      />
    </>
  );
};

export default Home;
